import { qLocal, qLocalAll } from "./xmlUtils";

// ── Pivot table colour resolver ──────────────────────────────────────────────
// Maps Excel's built-in pivot-table style names (e.g. "PivotStyleMedium2",
// "PivotStyleLight16", "PivotStyleDark4") to real CSS colours by reading the
// document's own theme accent palette instead of hard-coded Office defaults.
//
// Excel pivot styles have sub-variants within each family:
//   Style 1       → neutral (no accent)
//   Styles 2-7    → Variant A with Accent 1-6
//   Styles 8-13   → Variant B with Accent 1-6
//   Styles 14-19  → Variant C with Accent 1-6
//   Styles 20-25  → Variant D with Accent 1-6
//   Styles 26-28  → Variant E with Accent 1-3
// Each sub-variant has a different visual treatment (tint values, header fills, etc.)
export function resolvePivotColors(styleName, themeColors) {
  const OFFICE_ACCENTS = ["4472C4","ED7D31","A5A5A5","FFC000","5B9BD5","70AD47"];

  const numMatch = styleName.match(/(\d+)$/);
  const num      = numMatch ? parseInt(numMatch[1], 10) : 1;
  const isDark   = styleName.includes("Dark");
  const isMedium = styleName.includes("Medium");
  const isLight  = !isDark && !isMedium;

  // Determine sub-variant: 0=neutral, 1=A(2-7), 2=B(8-13), 3=C(14-19), 4=D(20-25), 5=E(26+)
  let subVariant = 0;
  if (num >= 2 && num <= 7)       subVariant = 1;
  else if (num >= 8 && num <= 13) subVariant = 2;
  else if (num >= 14 && num <= 19) subVariant = 3;
  else if (num >= 20 && num <= 25) subVariant = 4;
  else if (num >= 26)              subVariant = 5;

  // Resolve the accent base colour for this style number
  let accentHex = null;
  if (num >= 2) {
    const accentIdx = (num - 2) % 6;           // 0 = Accent1 … 5 = Accent6
    const themeIdx  = 4 + accentIdx;            // themeColors[4..9]
    const rawHex    = Array.isArray(themeColors) ? themeColors[themeIdx] : null;
    accentHex = rawHex ? `#${rawHex}` : `#${OFFICE_ACCENTS[accentIdx]}`;
  }

  // Blend hex colour toward white (t = 0 → original, t = 1 → white)
  const lighten = (hex, t) => {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const f = n => Math.min(255, Math.round(n + (255 - n) * t)).toString(16).padStart(2, "0");
    return `#${f(r)}${f(g)}${f(b)}`;
  };

  // ── Neutral / no-accent styles (Style 1 in each family) ────────────────────
  if (!accentHex) {
    if (isDark) {
      return {
        headerBg: "#595959", headerColor: "#FFFFFF",
        stripeBg: "#D9D9D9", borderColor: "#BFBFBF",
        grandTotalBg: "#404040", grandTotalColor: "#FFFFFF",
        headerBorderBottom: "", grandTotalBorderTop: "",
      };
    }
    if (isMedium) {
      return {
        headerBg: "#595959", headerColor: "#FFFFFF",
        stripeBg: "#D9D9D9", borderColor: "#BFBFBF",
        grandTotalBg: "#D9D9D9", grandTotalColor: "#000000",
        headerBorderBottom: "", grandTotalBorderTop: "2px solid #595959",
      };
    }
    // Light neutral
    return {
      headerBg: "", headerColor: "",
      stripeBg: "#F2F2F2", borderColor: "#BFBFBF",
      grandTotalBg: "", grandTotalColor: "",
      headerBorderBottom: "1px solid #BFBFBF", grandTotalBorderTop: "1px solid #BFBFBF",
    };
  }

  // ── Dark family ────────────────────────────────────────────────────────────
  if (isDark) {
    if (subVariant <= 1) {
      // Variant A (2-7): solid accent header, medium stripe
      return {
        headerBg: accentHex, headerColor: "#FFFFFF",
        stripeBg: lighten(accentHex, 0.50), borderColor: lighten(accentHex, 0.20),
        grandTotalBg: lighten(accentHex, 0.25), grandTotalColor: "#FFFFFF",
        headerBorderBottom: "", grandTotalBorderTop: "",
      };
    }
    // Variant B+ (8+): slightly lightened header, different stripe
    return {
      headerBg: lighten(accentHex, 0.15), headerColor: "#FFFFFF",
      stripeBg: lighten(accentHex, 0.60), borderColor: lighten(accentHex, 0.30),
      grandTotalBg: lighten(accentHex, 0.30), grandTotalColor: "#FFFFFF",
      headerBorderBottom: "", grandTotalBorderTop: "",
    };
  }

  // ── Medium family ──────────────────────────────────────────────────────────
  if (isMedium) {
    if (subVariant <= 1) {
      // Variant A (2-7): filled header with accent bg, light banded rows
      return {
        headerBg: accentHex, headerColor: "#FFFFFF",
        stripeBg: lighten(accentHex, 0.80), borderColor: lighten(accentHex, 0.42),
        grandTotalBg: lighten(accentHex, 0.60), grandTotalColor: "#000000",
        headerBorderBottom: "", grandTotalBorderTop: `2px solid ${accentHex}`,
      };
    }
    if (subVariant === 2) {
      // Variant B (8-13): lighter filled header, thinner bands
      return {
        headerBg: lighten(accentHex, 0.40), headerColor: "#FFFFFF",
        stripeBg: lighten(accentHex, 0.85), borderColor: lighten(accentHex, 0.50),
        grandTotalBg: lighten(accentHex, 0.60), grandTotalColor: "#000000",
        headerBorderBottom: "", grandTotalBorderTop: `2px solid ${lighten(accentHex, 0.40)}`,
      };
    }
    if (subVariant === 3) {
      // Variant C (14-19): NO header fill, accent borders, banded rows
      return {
        headerBg: "", headerColor: "",
        stripeBg: lighten(accentHex, 0.80), borderColor: accentHex,
        grandTotalBg: "", grandTotalColor: "",
        headerBorderBottom: `2px solid ${accentHex}`,
        grandTotalBorderTop: `2px solid ${accentHex}`,
      };
    }
    // Variant D+ (20+): softened accent header, banded
    return {
      headerBg: lighten(accentHex, 0.20), headerColor: "#FFFFFF",
      stripeBg: lighten(accentHex, 0.85), borderColor: lighten(accentHex, 0.55),
      grandTotalBg: lighten(accentHex, 0.60), grandTotalColor: "#000000",
      headerBorderBottom: "", grandTotalBorderTop: `2px solid ${lighten(accentHex, 0.20)}`,
    };
  }

  // ── Light family ───────────────────────────────────────────────────────────
  if (subVariant <= 1) {
    // Variant A (2-7): very subtle — thin accent borders, no header fill, faint stripe
    return {
      headerBg: "", headerColor: "",
      stripeBg: lighten(accentHex, 0.90), borderColor: lighten(accentHex, 0.55),
      grandTotalBg: "", grandTotalColor: "",
      headerBorderBottom: `1px solid ${lighten(accentHex, 0.40)}`,
      grandTotalBorderTop: `1px solid ${lighten(accentHex, 0.40)}`,
    };
  }
  if (subVariant === 2) {
    // Variant B (8-13): no header fill, accent bottom-border on header, light stripe
    return {
      headerBg: "", headerColor: "",
      stripeBg: lighten(accentHex, 0.82), borderColor: lighten(accentHex, 0.55),
      grandTotalBg: "", grandTotalColor: "",
      headerBorderBottom: `2px solid ${accentHex}`,
      grandTotalBorderTop: `2px solid ${accentHex}`,
    };
  }
  if (subVariant === 3) {
    // Variant C (14-19): FILLED header with accent bg and white text — this is
    // the default PivotStyleLight16 look. Excel renders these with a solid
    // coloured header row, unlike the other Light sub-variants.
    return {
      headerBg: accentHex, headerColor: "#FFFFFF",
      stripeBg: lighten(accentHex, 0.82), borderColor: lighten(accentHex, 0.55),
      grandTotalBg: lighten(accentHex, 0.60), grandTotalColor: "#000000",
      headerBorderBottom: "", grandTotalBorderTop: `2px solid ${accentHex}`,
    };
  }
  // Variant D+ (20+): no header fill, accent double-border, medium stripe
  return {
    headerBg: "", headerColor: "",
    stripeBg: lighten(accentHex, 0.85), borderColor: lighten(accentHex, 0.50),
    grandTotalBg: "", grandTotalColor: "",
    headerBorderBottom: `2px solid ${accentHex}`,
    grandTotalBorderTop: `2px solid ${accentHex}`,
  };
}

// ── Helper: normalise a Target path from a rels file ─────────────────────────
// Handles both relative ("../pivotTables/pt1.xml") and absolute ("/xl/…") forms.
function normaliseRelTarget(target, baseXlPrefix = "xl/") {
  if (!target) return null;
  // Absolute path starting with /xl/
  if (target.startsWith("/xl/")) return target.slice(1); // strip leading /
  if (target.startsWith("/"))    return target.slice(1);
  // Strip leading ../
  let t = target;
  while (t.startsWith("../")) t = t.slice(3);
  return `${baseXlPrefix}${t}`;
}

// ── Parse a single pivot XML and return a descriptor object ──────────────────
function parsePivotXml(pivotDoc, pivotPath) {
  const pivotRoot = qLocal(pivotDoc, "pivotTableDefinition");
  const pivotName = pivotRoot?.getAttribute("name") || "PivotTable";

  const locationNode = qLocal(pivotDoc, "location");
  const ref = locationNode?.getAttribute("ref");
  if (!ref) return null;

  const parseCell = (cellRef) => {
    const match = cellRef.match(/^([A-Za-z]+)(\d+)$/);
    if (!match) return { r: 0, c: 0 };
    let col = 0;
    for (let i = 0; i < match[1].length; i++) {
      col = col * 26 + (match[1].toUpperCase().charCodeAt(i) - 64);
    }
    return { r: parseInt(match[2]) - 1, c: col - 1 };
  };

  const rangeParts = ref.split(":");
  const tl = parseCell(rangeParts[0]);
  const br = rangeParts[1] ? parseCell(rangeParts[1]) : tl;

  const firstHeaderRow = parseInt(locationNode?.getAttribute("firstHeaderRow") ?? "1");
  const firstDataRow   = parseInt(locationNode?.getAttribute("firstDataRow")   ?? "1");
  const firstDataCol   = parseInt(locationNode?.getAttribute("firstDataCol")   ?? "1");

  // Grand total attributes from pivotTableDefinition
  const rowGrandTotals = pivotRoot?.getAttribute("rowGrandTotals") !== "0"; // default true
  const colGrandTotals = pivotRoot?.getAttribute("colGrandTotals") !== "0"; // default true

  const styleInfo        = qLocal(pivotDoc, "pivotTableStyleInfo");
  const styleName        = styleInfo?.getAttribute("name") || "PivotStyleLight16";
  const showRowHeaders   = styleInfo?.getAttribute("showRowHeaders") !== "0";
  const showColHeaders   = styleInfo?.getAttribute("showColHeaders") !== "0";
  const showRowStripes   = styleInfo?.getAttribute("showRowStripes") === "1";
  const showColStripes   = styleInfo?.getAttribute("showColStripes") === "1";
  const showLastColumn   = styleInfo?.getAttribute("showLastColumn") !== "0";  // grand total col highlight
  const showFirstColumn  = styleInfo?.getAttribute("showFirstColumn") !== "0"; // row label highlight

  const pivotFormats = [];
  const formatsNode = qLocal(pivotDoc, "formats");
  if (formatsNode) {
    qLocalAll(formatsNode, "format").forEach(fmt => {
      const dxfId = fmt.getAttribute("dxfId");
      if (dxfId != null) pivotFormats.push({ dxfId: parseInt(dxfId, 10) });
    });
  }

  return {
    name: pivotName,
    ref,
    range: { r1: tl.r, c1: tl.c, r2: br.r, c2: br.c },
    firstHeaderRow,
    firstDataRow,
    firstDataCol,
    styleName,
    showRowHeaders,
    showColHeaders,
    showRowStripes,
    showColStripes,
    showLastColumn,
    showFirstColumn,
    rowGrandTotals,
    colGrandTotals,
    pivotFormats,
  };
}

// Parse OpenXML pivot table definitions to extract cell ranges and style info.
// Uses two independent strategies so that either one succeeding is sufficient.
export async function extractPivotTablesFromZip(zip) {
  const pivotsBySheet = {};
  try {
    // ── Strategy A: follow workbook → sheet → sheet.rels → pivot ─────────────
    await _strategyA(zip, pivotsBySheet);

    // ── Strategy B: enumerate pivot XML files directly and look for the sheet ─
    // Runs whenever strategy A found nothing, as a fallback.
    if (Object.keys(pivotsBySheet).length === 0) {
      await _strategyB(zip, pivotsBySheet);
    }
  } catch (err) {
    console.warn("[PivotDetect] Extraction failed:", err);
  }

  console.log("[PivotDetect] pivotsBySheet result:", JSON.stringify(
    Object.fromEntries(Object.entries(pivotsBySheet).map(([k, v]) => [k, v.map(p => ({
      name: p.name, ref: p.ref, styleName: p.styleName,
      firstDataRow: p.firstDataRow, firstDataCol: p.firstDataCol,
    }))]))
  ));
  return pivotsBySheet;
}

// Strategy A: workbook.xml → sheet map → sheet rels → pivot paths → pivot XML
async function _strategyA(zip, pivotsBySheet) {
  const pivotFiles = Object.keys(zip.files).filter(f =>
    /^xl\/pivotTables\/pivotTable\d+\.xml$/i.test(f)
  );
  if (pivotFiles.length === 0) return; // no pivot tables in this workbook

  const wbXml = await zip.file("xl/workbook.xml")?.async("string");
  if (!wbXml) return;
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");

  const RELS_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
  const sheetIdToName = {};
  qLocalAll(wbDoc, "sheet").forEach(node => {
    const rId      = node.getAttributeNS(RELS_NS, "id") || node.getAttribute("r:id");
    const sheetName = node.getAttribute("name");
    if (rId && sheetName) sheetIdToName[rId] = sheetName;
  });
  console.log("[PivotDetect:A] sheetIdToName:", JSON.stringify(sheetIdToName));

  const wbRelsXml = await zip.file("xl/_rels/workbook.xml.rels")?.async("string");
  if (!wbRelsXml) return;
  const wbRelsDoc = new DOMParser().parseFromString(wbRelsXml, "application/xml");

  // Map normalised sheet path → sheet name
  const sheetPathToName = {};
  qLocalAll(wbRelsDoc, "Relationship").forEach(rel => {
    const rawTarget = rel.getAttribute("Target") || "";
    const id = rel.getAttribute("Id");
    if (!sheetIdToName[id]) return;
    // Normalise: absolute (/xl/worksheets/…) or relative (worksheets/…)
    let target = rawTarget.replace(/^\//, ""); // strip leading /
    // If the path already starts with xl/, use as-is; otherwise prepend nothing
    // (targets in workbook.xml.rels are relative to xl/, e.g. "worksheets/sheet1.xml")
    sheetPathToName[target] = sheetIdToName[id];
  });
  console.log("[PivotDetect:A] sheetPathToName:", JSON.stringify(sheetPathToName));

  for (const sheetPath of Object.keys(sheetPathToName)) {
    const sheetName = sheetPathToName[sheetPath];

    // Build the rels path for this sheet
    // sheetPath is relative to xl/, e.g. "worksheets/sheet1.xml"
    const parts = sheetPath.split("/");
    const filename = parts.pop();
    const sheetRelsPath = `xl/${parts.join("/")}/_rels/${filename}.rels`
      .replace(/\/+/g, "/"); // collapse any accidental double slashes

    const sheetRelsFile = zip.file(sheetRelsPath);
    if (!sheetRelsFile) {
      console.log("[PivotDetect:A] no rels file at", sheetRelsPath);
      continue;
    }

    const relsXml = await sheetRelsFile.async("string");
    const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");

    const pivotPaths = [];
    qLocalAll(relsDoc, "Relationship").forEach(rel => {
      const type = rel.getAttribute("Type") || "";
      if (!type.includes("/pivotTable")) return;
      const rawTarget = rel.getAttribute("Target") || "";
      // Targets in sheet.xml.rels are relative to xl/worksheets/, e.g. "../pivotTables/pt1.xml"
      const pivotPath = normaliseRelTarget(rawTarget, "xl/worksheets/");
      if (pivotPath) pivotPaths.push(pivotPath);
    });

    if (pivotPaths.length === 0) continue;
    if (!pivotsBySheet[sheetName]) pivotsBySheet[sheetName] = [];

    for (const pivotPath of pivotPaths) {
      const pivotFile = zip.file(pivotPath);
      if (!pivotFile) {
        console.log("[PivotDetect:A] pivot file not found at", pivotPath);
        continue;
      }
      const pivotXml = await pivotFile.async("string");
      const pivotDoc = new DOMParser().parseFromString(pivotXml, "application/xml");
      const descriptor = parsePivotXml(pivotDoc, pivotPath);
      if (descriptor) {
        console.log("[PivotDetect:A] found pivot on sheet", sheetName, ":", descriptor.ref);
        pivotsBySheet[sheetName].push(descriptor);
      }
    }
  }
}

// Strategy B: enumerate xl/pivotTables/*.xml directly; find the owning sheet
// by scanning each sheet's rels file for a reference to that pivot.
async function _strategyB(zip, pivotsBySheet) {
  const pivotFiles = Object.keys(zip.files).filter(f =>
    /^xl\/pivotTables\/pivotTable\d+\.xml$/i.test(f)
  );
  if (pivotFiles.length === 0) return;

  // Find all sheet rels files in the zip
  const sheetRelsFiles = Object.keys(zip.files).filter(f =>
    /^xl\/worksheets\/_rels\/sheet\d+\.xml\.rels$/i.test(f)
  );

  // Build a reverse map: pivotFile → sheet name
  // We need sheet names; get them from workbook.xml
  const sheetIndexToName = {};
  try {
    const wbXml = await zip.file("xl/workbook.xml")?.async("string");
    if (wbXml) {
      const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
      qLocalAll(wbDoc, "sheet").forEach((node, idx) => {
        sheetIndexToName[idx + 1] = node.getAttribute("name") || `Sheet${idx + 1}`;
      });
    }
  } catch { /* ignore */ }

  for (const pivotFilePath of pivotFiles) {
    const pivotFile = zip.file(pivotFilePath);
    if (!pivotFile) continue;

    const pivotXml = await pivotFile.async("string");
    const pivotDoc = new DOMParser().parseFromString(pivotXml, "application/xml");
    const descriptor = parsePivotXml(pivotDoc, pivotFilePath);
    if (!descriptor) continue;

    // Search each sheet rels for a reference to this pivot file
    let foundSheet = null;
    for (const relsPath of sheetRelsFiles) {
      const relsFile = zip.file(relsPath);
      if (!relsFile) continue;
      const relsXml = await relsFile.async("string");
      const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");

      const refs = qLocalAll(relsDoc, "Relationship").filter(rel => {
        const type = rel.getAttribute("Type") || "";
        if (!type.includes("/pivotTable")) return false;
        const rawTarget = rel.getAttribute("Target") || "";
        const resolved = normaliseRelTarget(rawTarget, "xl/worksheets/");
        return resolved === pivotFilePath;
      });

      if (refs.length > 0) {
        // Derive sheet name from rels path like xl/worksheets/_rels/sheet2.xml.rels
        const m = relsPath.match(/sheet(\d+)\.xml\.rels$/i);
        const idx = m ? parseInt(m[1]) : null;
        foundSheet = (idx && sheetIndexToName[idx]) || null;
        if (foundSheet) break;
      }
    }

    if (foundSheet) {
      if (!pivotsBySheet[foundSheet]) pivotsBySheet[foundSheet] = [];
      pivotsBySheet[foundSheet].push(descriptor);
      console.log("[PivotDetect:B] found pivot on sheet", foundSheet, ":", descriptor.ref);
    } else {
      console.log("[PivotDetect:B] could not map pivot to sheet:", pivotFilePath);
    }
  }
}
